import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, Radio, Space, Typography, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { saveFile } from "../../../native/files";
import { errorText, exportGaeb, sourceFile } from "./api";
import "./gaeb.css";

const { Text } = Typography;

const euro = cents =>
  (Number(cents || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" });

/**
 * "Export GAEB" of an offer: X84 (Angebotsabgabe, unit prices and totals) for an imported LV,
 * X83/X81 (texts, no prices) to hand an LV on. Shows what the server reported (unpriced
 * positions, positions that are not part of the LV) after the download.
 */
const GaebExportModal = ({ offer, open, onClose }) => {
  const { t } = useTranslation("dashboard");
  const [phase, setPhase] = useState("84");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setPhase("84");
      setResult(null);
    }
  }, [open]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await exportGaeb(offer._id, phase);
      await saveFile(new Blob([res.data.content], { type: "application/xml" }), res.data.fileName);
      setResult(res.data);
      message.success(t("{{file}} saved", { file: res.data.fileName }));
    } catch (err) {
      message.error(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const original = async () => {
    try {
      const res = await sourceFile(offer._id);
      await saveFile(res.data, offer.gaeb?.fileName || "lv.x83");
    } catch (err) {
      message.error(errorText(err));
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`${t("Export GAEB")} · ${offer?.offerNumber || offer?.quotationNumber || ""} ${offer?.title || ""}`}
      width={620}
      destroyOnClose
      footer={[
        <Button key="close" onClick={onClose}>
          {t("Close")}
        </Button>,
        <Button
          key="dl"
          type="primary"
          icon={<DownloadOutlined />}
          loading={busy}
          onClick={download}
        >
          {t("Download")}
        </Button>
      ]}
    >
      <div className="gaeb-ui">
        {offer?.gaeb?.sourceFormat ? (
          <Text type="secondary">
            {t("Imported from {{file}} ({{format}})", {
              file: offer.gaeb.fileName,
              format: offer.gaeb.sourceFormat
            })}
          </Text>
        ) : (
          <Text type="secondary">
            {t(
              "This offer was not imported; it is exported as a simple LV with positions 0010, 0020, …"
            )}
          </Text>
        )}
        <Radio.Group
          className="gaeb-ui__block gaeb-phases"
          value={phase}
          onChange={e => setPhase(e.target.value)}
        >
          <Space direction="vertical">
            <Radio value="84">
              <strong>X84</strong> {t("Bid submission with your unit prices and totals")}
            </Radio>
            <Radio value="83">
              <strong>X83</strong> {t("Request for quotation: texts and quantities, no prices")}
            </Radio>
            <Radio value="81">
              <strong>X81</strong> {t("Bill of quantities: texts and quantities, no prices")}
            </Radio>
          </Space>
        </Radio.Group>
        {result && (
          <div className="gaeb-ui__block">
            <Alert
              type={result.warnings.length ? "warning" : "success"}
              showIcon
              message={t("{{count}} positions exported, net total {{total}}", {
                count: result.positions,
                total: euro(result.totals.net)
              })}
              description={
                result.warnings.length ? (
                  <ul className="gaeb-ui__warnings">
                    {result.warnings.slice(0, 20).map((w, i) => (
                      <li key={i}>{w.message}</li>
                    ))}
                  </ul>
                ) : null
              }
            />
          </div>
        )}
        {offer?.gaeb?.importedFileId ? (
          <Button type="link" className="gaeb-ui__block" onClick={original}>
            {t("Download the imported file")}
          </Button>
        ) : null}
      </div>
    </Modal>
  );
};

export default GaebExportModal;
